import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { formatPln, parsePln } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { QuickPicker } from '../ui/QuickPicker';
import { errorText, PURCHASES_KEY, TARGET_LABELS, type PurchaseDetail, type Target } from './types';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

type Kind = 'serialized' | 'bulk' | 'consumable';
const KINDS: Record<Kind, string> = { serialized: 'Z egzemplarzami', bulk: 'Ilościowy', consumable: 'Zużywalny' };
type Draft = {
  key: number;
  target: Target;
  net: string;
  quantity: string;
  item: { id: string; name: string } | null;
  name: string;
  manufacturer: string;
  model: string;
  kind: Kind;
  invName: string;
  invCategory: string;
  description: string;
};
let nextKey = 1;
const blank = (target: Target = 'katalog'): Draft => ({ key: nextKey++, target, net: '', quantity: '1', item: null, name: '', manufacturer: '', model: '', kind: 'serialized', invName: '', invCategory: '', description: '' });
const grosze = (text: string): number | null => {
  try {
    return text.trim() === '' ? null : parsePln(text);
  } catch {
    return null;
  }
};

const plnText = (grosze: number) => (grosze / 100).toFixed(2).replace('.', ',');

/** F41: a KSeF invoice starts with one part per line (name, whole quantity, net); without amounts or a matching sum, one part. */
export function fromLines(purchase: PurchaseDetail): Draft[] | null {
  const lines = purchase.lines ?? [];
  if (lines.length === 0 || lines.some((l) => l.netGrosze === undefined)) return null;
  // lines that do not add up to the invoice's net (e.g. a gross-priced invoice read without rates): one part
  if (lines.reduce((a, l) => a + l.netGrosze!, 0) !== purchase.netGrosze) return null;
  return lines.map((l) => {
    const q = Number(l.quantity);
    return { ...blank(), net: plnText(l.netGrosze!), quantity: Number.isInteger(q) && q > 0 ? String(q) : '1', name: l.name, invName: l.name, description: l.name };
  });
}

const searchItems = async (q: string, signal: AbortSignal) =>
  (await api<{ items: { id: string; name: string; manufacturer?: string; model?: string }[] }>(`/api/catalog/items/suggest?q=${encodeURIComponent(q)}`, { signal })).items.map((i) => ({ key: i.id, name: i.name, details: [[i.manufacturer, i.model].filter(Boolean).join(' ')].filter(Boolean) }));

function body(d: Draft) {
  const netGrosze = parsePln(d.net);
  if (d.target === 'koszt') return { target: d.target, netGrosze, description: d.description };
  if (d.target === 'inwestycja') return { target: d.target, netGrosze, investment: { name: d.invName, category: d.invCategory }, description: d.description };
  const quantity = Number(d.quantity);
  return d.item
    ? { target: d.target, netGrosze, quantity, catalogItemId: d.item.id, description: d.description }
    : { target: d.target, netGrosze, quantity, newItem: { name: d.name, manufacturer: d.manufacturer, model: d.model, kind: d.kind }, description: d.description };
}

/**
 * "Rozpisz" (F42): the invoice's net split into parts — to the catalog (an existing item or a new one), an investment,
 * or a plain cost. The parts must add up to the net; what is left is shown under the list.
 */
export function AllocateDialog({ purchase, onClose }: { purchase: PurchaseDetail; onClose: () => void }) {
  const queryClient = useQueryClient();
  const net = purchase.netGrosze ?? 0;
  const [parts, setParts] = useState<Draft[]>(() => fromLines(purchase) ?? [{ ...blank(), net: (net / 100).toFixed(2).replace('.', ',') }]);
  const [local, setLocal] = useState('');
  const set = (key: number, patch: Partial<Draft>) => setParts((list) => list.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  const sum = parts.reduce((a, p) => a + (grosze(p.net) ?? 0), 0);
  const left = net - sum;
  const save = useMutation({
    mutationFn: () => api<PurchaseDetail>(`/api/purchases/${purchase.id}/allocate`, { method: 'POST', body: { version: purchase.version, parts: parts.map(body) } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PURCHASES_KEY });
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
      onClose();
    },
  });
  function ready(): boolean {
    if (parts.some((p) => grosze(p.net) === null)) return (setLocal('Podaj kwotę netto każdej części'), false);
    if (left !== 0) return (setLocal(`Części muszą dać razem netto faktury: zostało ${formatPln(left)}`), false);
    setLocal('');
    return true;
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (ready()) save.mutate();
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const plain = (ps: Draft[]) => ps.map(({ key: _key, ...p }) => p);
  const opening = useOpening(plain(parts));
  const guardRef = useUnsavedGuard({
    dirty: !sameValues(plain(parts), opening),
    save: async () => {
      if (!ready()) return false;
      await save.mutateAsync();
      return true;
    },
  });
  const message = local || (save.error ? (save.error instanceof ApiError && save.error.status === 409 ? 'Ktoś w międzyczasie zmienił tę fakturę — zamknij i otwórz ją ponownie' : errorText(save.error, 'Nie udało się rozpisać faktury')) : '');
  return (
    <form ref={guardRef} className="allocate" role="dialog" aria-label="Rozpisz fakturę" onSubmit={submit} data-feature-id="purchases.allocate">
      <DialogLayer onClose={onClose} busy={save.isPending} />
      <h2>Rozpisz fakturę</h2>
      <ol className="allocate-parts">
        {parts.map((p, i) => (
          <li key={p.key} aria-label={`Część ${i + 1}`} className="allocate-part">
            <div className="segment" role="group" aria-label={`Część ${i + 1}: dokąd`}>
              {(Object.keys(TARGET_LABELS) as Target[]).map((t) => (
                <button key={t} type="button" aria-pressed={p.target === t} onClick={() => set(p.key, { target: t })}>
                  {TARGET_LABELS[t]}
                </button>
              ))}
            </div>
            <div className="allocate-fields">
              {p.target === 'katalog' &&
                (p.item ? (
                  <p className="allocate-item">
                    Pozycja: <strong>{p.item.name}</strong>{' '}
                    <button className="btn small ghost" type="button" onClick={() => set(p.key, { item: null })}>
                      Zmień
                    </button>
                  </p>
                ) : (
                  <>
                    <QuickPicker label="Istniejąca pozycja katalogu" placeholder="szukaj w katalogu…" search={searchItems} onPick={(o) => set(p.key, { item: { id: o.key, name: o.name } })} />
                    <span className="muted allocate-or">albo nowa:</span>
                    <label className="field">
                      <span>Nazwa nowej pozycji</span>
                      <input value={p.name} onChange={(e) => set(p.key, { name: e.target.value })} autoComplete="off" />
                    </label>
                    <label className="field">
                      <span>Producent</span>
                      <input value={p.manufacturer} onChange={(e) => set(p.key, { manufacturer: e.target.value })} autoComplete="off" />
                    </label>
                    <label className="field">
                      <span>Model</span>
                      <input value={p.model} onChange={(e) => set(p.key, { model: e.target.value })} autoComplete="off" />
                    </label>
                    <label className="field">
                      <span>Rodzaj pozycji</span>
                      <select value={p.kind} onChange={(e) => set(p.key, { kind: e.target.value as Kind })}>
                        {(Object.keys(KINDS) as Kind[]).map((k) => (
                          <option key={k} value={k}>
                            {KINDS[k]}
                          </option>
                        ))}
                      </select>
                    </label>
                  </>
                ))}
              {p.target === 'katalog' && (
                <label className="field narrow">
                  <span>Ilość</span>
                  <NumberInput kind="integer" value={p.quantity} onValueChange={(quantity) => set(p.key, { quantity })} />
                </label>
              )}
              {p.target === 'inwestycja' && (
                <>
                  <label className="field">
                    <span>Nazwa inwestycji</span>
                    <input value={p.invName} onChange={(e) => set(p.key, { invName: e.target.value })} autoComplete="off" />
                  </label>
                  <label className="field">
                    <span>Kategoria</span>
                    <input value={p.invCategory} onChange={(e) => set(p.key, { invCategory: e.target.value })} placeholder="np. Transport" autoComplete="off" />
                  </label>
                </>
              )}
              {p.target === 'koszt' && (
                <label className="field">
                  <span>Opis</span>
                  <input value={p.description} onChange={(e) => set(p.key, { description: e.target.value })} placeholder="np. transport" autoComplete="off" />
                </label>
              )}
              <label className="field">
                <span>Netto części (zł)</span>
                <NumberInput kind="money" value={p.net} onValueChange={(v) => set(p.key, { net: v })} />
              </label>
              {parts.length > 1 && (
                <button className="btn icon ghost" type="button" aria-label={`Usuń część ${i + 1}`} title="Usuń" onClick={() => setParts((list) => list.filter((x) => x.key !== p.key))}>
                  ×
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
      <p className={left === 0 ? 'muted' : 'fin-warn'} role="status" aria-label="Suma części">
        Netto faktury {formatPln(net)} · rozpisano {formatPln(sum)} · {left === 0 ? 'wszystko rozpisane' : `zostało ${formatPln(left)}`}
      </p>
      <div className="actions row-actions">
        <button className="btn" type="button" onClick={() => setParts((list) => [...list, { ...blank(), net: left > 0 ? (left / 100).toFixed(2).replace('.', ',') : '' }])}>
          Dodaj część
        </button>
        <button className="btn primary" type="submit" disabled={save.isPending}>
          Rozpisz
        </button>
        <button className="btn ghost" type="button" onClick={onClose}>
          Anuluj
        </button>
      </div>
      {message && (
        <p className="alert" role="alert">
          {message}
        </p>
      )}
    </form>
  );
}
